import { formatPeakVolumePriceLabel } from '@/utils/peakVolumePriceLayer';

const PEAK_VOL_STROKE = '#f97316';
/** Gap from plot right edge to the HTML pill (drawn outside SVG clip). */
export const PEAK_VOL_PILL_GAP_PX = 14;
/** How far the SVG line extends past the plot edge before the pill. */
const PEAK_VOL_LINE_PAST_PLOT_PX = 10;
const PEAK_VOL_PILL_H = 22;

/** Recharts band/category scale: callable plus optional d3-style methods on the function. */
type ChartBandScale = ((v: string) => number) & {
  domain?: () => string[];
  bandwidth?: () => number;
};

export type PeakVolumeChartMetrics = {
  xAxisMap?: Record<string, { scale?: ChartBandScale }>;
  yAxisMap?: Record<string, { scale?: (v: number) => number }>;
  offset?: { left: number; top: number; width: number; height: number };
};

type BarRow = {
  date?: string;
  time?: string;
  high?: number;
  low?: number;
  close?: number;
  price?: number;
};

type GuideSvgProps = {
  price: number;
  chartData: BarRow[];
  categoryKey: 'date' | 'time';
  chartProps: PeakVolumeChartMetrics;
};

/**
 * In-chart SVG segment: from the price cross on the series to slightly past the
 * plot edge. The pill label is HTML in the gutter (see `peakVolumeGuideOverlayPosition`).
 */
export function PeakVolumePriceGuideSvg({
  price,
  chartData,
  categoryKey,
  chartProps,
}: GuideSvgProps) {
  const offset = chartProps.offset;
  const yAxisMap = chartProps.yAxisMap;
  if (!offset || !yAxisMap || chartData.length === 0) return null;

  const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  const yScale = yAxis?.scale;
  const xAxis = chartProps.xAxisMap?.[0] ?? Object.values(chartProps.xAxisMap ?? {})[0];
  const xScale = xAxis?.scale;
  if (typeof yScale !== 'function' || typeof xScale !== 'function') return null;

  const yPlot = yScale(price);
  if (yPlot == null || Number.isNaN(Number(yPlot))) return null;

  const y = offset.top + Number(yPlot);
  const plotRight = offset.left + offset.width;
  const categories = chartData.map((row) => String(row[categoryKey] ?? '').trim()).filter(Boolean);
  const xStart = peakVolumeGuideLineStartX(
    chartData,
    categories,
    categoryKey,
    price,
    xScale,
    offset.left,
    offset.width,
  );
  const xLineStart = Math.min(xStart, plotRight - 1);
  const lineEnd = plotRight + PEAK_VOL_LINE_PAST_PLOT_PX;

  if (lineEnd <= xLineStart + 2) return null;

  return (
    <g className="recharts-layer peak-volume-guide" pointerEvents="none" style={{ overflow: 'visible' }}>
      <line
        x1={xLineStart}
        y1={y}
        x2={lineEnd}
        y2={y}
        stroke={PEAK_VOL_STROKE}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <circle cx={xLineStart} cy={y} r={2.5} fill={PEAK_VOL_STROKE} fillOpacity={0.35} />
    </g>
  );
}

/** HTML pill in the chart gutter (not clipped by Recharts SVG). */
export function PeakVolumePriceGuidePill({
  label,
  left,
  top,
}: {
  label: string;
  left: number;
  top: number;
}) {
  return (
    <div
      className="pointer-events-none absolute z-[18] flex items-center justify-center whitespace-nowrap rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-900 shadow-sm"
      style={{ left, top, fontFamily: 'system-ui, sans-serif' }}
    >
      {label}
    </div>
  );
}

export function peakVolumeGuideLabel(price: number): string {
  return `Peak vol ${formatPeakVolumePriceLabel(price)}`;
}

export function peakVolumeGuidePlotY(
  chartProps: PeakVolumeChartMetrics,
  price: number,
): { plotRight: number; y: number; plotTop: number; plotBottom: number } | null {
  const offset = chartProps.offset;
  const yAxisMap = chartProps.yAxisMap;
  if (!offset || !yAxisMap) return null;
  const yAxis = yAxisMap['price'] ?? yAxisMap[0] ?? Object.values(yAxisMap)[0];
  const yScale = yAxis?.scale;
  if (typeof yScale !== 'function') return null;
  const yPlot = yScale(price);
  if (yPlot == null || Number.isNaN(Number(yPlot))) return null;
  return {
    plotRight: offset.left + offset.width,
    y: offset.top + Number(yPlot),
    plotTop: offset.top,
    plotBottom: offset.top + offset.height,
  };
}

/** Client position for the peak-volume pill in the right gutter. */
export function peakVolumeGuideOverlayPosition(
  svg: SVGSVGElement,
  wrap: HTMLElement,
  plotRight: number,
  horizontalY: number,
  plotTop: number,
  plotBottom: number,
  pillH: number = PEAK_VOL_PILL_H,
): { left: number; top: number } | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = plotRight + PEAK_VOL_PILL_GAP_PX;
  pt.y = horizontalY;
  const scr = pt.matrixTransform(ctm);
  const wrapRect = wrap.getBoundingClientRect();
  const left = scr.x - wrapRect.left;
  let top = scr.y - wrapRect.top - pillH / 2;

  const ptMin = svg.createSVGPoint();
  ptMin.x = plotRight;
  ptMin.y = plotTop;
  const scrMin = ptMin.matrixTransform(ctm);
  const ptMax = svg.createSVGPoint();
  ptMax.x = plotRight;
  ptMax.y = plotBottom;
  const scrMax = ptMax.matrixTransform(ctm);
  const minTop = scrMin.y - wrapRect.top;
  const maxTop = scrMax.y - wrapRect.top - pillH;
  top = Math.max(minTop, Math.min(top, maxTop));

  return { left, top };
}

function peakVolumeGuideLineStartX(
  chartData: BarRow[],
  categories: string[],
  categoryKey: 'date' | 'time',
  peakPrice: number,
  xScale: ChartBandScale,
  offsetLeft: number,
  plotWidth: number,
): number {
  const idx = peakVolumeGuideStartIndex(chartData, peakPrice);
  for (let i = Math.max(1, idx); i < chartData.length; i++) {
    const prev = finiteBarPrice(chartData[i - 1].close, chartData[i - 1].price);
    const cur = finiteBarPrice(chartData[i].close, chartData[i].price);
    if (prev > peakPrice && cur <= peakPrice) {
      const span = prev - cur;
      const t = span > 1e-6 ? (prev - peakPrice) / span : 0.5;
      const catPrev = String(chartData[i - 1][categoryKey] ?? '');
      const catCur = String(chartData[i][categoryKey] ?? '');
      const x0 = bandCenterX(catPrev, categories, xScale, offsetLeft, plotWidth);
      const x1 = bandCenterX(catCur, categories, xScale, offsetLeft, plotWidth);
      return x0 + (x1 - x0) * t;
    }
  }
  const cat = String(chartData[idx]?.[categoryKey] ?? categories[idx] ?? '');
  return bandCenterX(cat, categories, xScale, offsetLeft, plotWidth);
}

function bandCenterX(
  category: string,
  categories: string[],
  xScale: ChartBandScale,
  offsetLeft: number,
  plotWidth: number,
): number {
  let xBand = xScale(category);
  if (xBand == null || Number.isNaN(Number(xBand))) {
    const domain = typeof xScale.domain === 'function' ? (xScale.domain() as string[]) : [];
    const match = domain.find((d) => String(d) === String(category));
    if (match != null) xBand = xScale(match);
  }
  if (xBand == null || Number.isNaN(Number(xBand))) {
    const idx = categories.indexOf(category);
    const ratio = categories.length > 1 && idx >= 0 ? idx / (categories.length - 1) : 1;
    xBand = plotWidth * ratio;
  }
  const bw = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
  return offsetLeft + Number(xBand) + (bw > 0 ? bw / 2 : 0);
}

/**
 * Where the guide line begins: latest descent through the peak-volume price
 * on the visible series (right leg of the move).
 */
export function peakVolumeGuideStartIndex(
  data: Array<{ high?: number; low?: number; close?: number; price?: number }>,
  peakPrice: number,
): number {
  const tol = 0.12;
  for (let i = data.length - 1; i >= 1; i--) {
    const prev = finiteBarPrice(data[i - 1].close, data[i - 1].price);
    const cur = finiteBarPrice(data[i].close, data[i].price);
    if (prev > peakPrice + tol && cur <= peakPrice + tol && cur >= peakPrice - tol) {
      return i;
    }
  }
  return lastBarIndexAtPeakVolumePrice(data, peakPrice);
}

/** Rightmost bar whose range includes the peak volume price. */
export function lastBarIndexAtPeakVolumePrice(
  data: Array<{ high?: number; low?: number; close?: number; price?: number }>,
  peakPrice: number,
): number {
  for (let i = data.length - 1; i >= 0; i--) {
    const row = data[i];
    const high = finiteBarPrice(row.high, row.price ?? row.close);
    const low = finiteBarPrice(row.low, row.price ?? row.close);
    if (high >= peakPrice && low <= peakPrice) return i;
  }
  for (let i = data.length - 1; i >= 0; i--) {
    const high = finiteBarPrice(data[i].high, data[i].price ?? data[i].close);
    if (high >= peakPrice) return i;
  }
  return Math.max(0, data.length - 1);
}

/** Leftmost bar whose range includes the peak volume price (intraday). */
export function firstBarIndexAtPeakVolumePrice(
  data: Array<{ high?: number; low?: number; close?: number; price?: number }>,
  peakPrice: number,
): number {
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const high = finiteBarPrice(row.high, row.price ?? row.close);
    const low = finiteBarPrice(row.low, row.price ?? row.close);
    if (high >= peakPrice && low <= peakPrice) return i;
  }
  for (let i = 0; i < data.length; i++) {
    const high = finiteBarPrice(data[i].high, data[i].price ?? data[i].close);
    if (high >= peakPrice) return i;
  }
  return 0;
}

function finiteBarPrice(primary: unknown, fallback: unknown): number {
  if (typeof primary === 'number' && Number.isFinite(primary)) return primary;
  if (typeof fallback === 'number' && Number.isFinite(fallback)) return fallback;
  return NaN;
}
